"use client";

import { useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { ChevronLeft, ChevronRight, Download, Eye, FilterX, MessageSquare, Plus, Trash2, X } from "lucide-react";
import { Link, Navigate, useNavigate, useParams } from "react-router-dom";

import { EmptyState, ListCount, ListSurface, ListSurfaceBody, ListSurfaceFooter, ListSurfaceHeader, ListToolbar, ListToolbarGroup, SkeletonTableRow, ToolbarIconButton } from "@/components/ds";
import { Modal } from "@/components/ds/modal";
import { EnterprisePageShell } from "@/components/layout/enterprise-page-shell";
import { FlashToast } from "@/components/ui-system/flash-toast";
import { tableHeaderSearchInputClass, tableHeaderSelectTriggerClass, ThemedSelect, themedSelectMenuClass } from "@/components/ui-system/select";
import {
  addSupportTicketMessage,
  createSupportTicketExpense,
  createSupportTicket,
  deleteSupportTicket,
  downloadExpenseReceipt,
  downloadSupportReport,
  downloadSupportTicketAttachment,
  getSupportTicket,
  listSupportAssignees,
  listSupportExpenseCategories,
  listSupportAvailableDevices,
  listSupportTickets,
  markSupportTicketRead,
  updateSupportTicket,
  updateSupportTicketExpense,
  uploadExpenseReceipt,
  uploadSupportReport,
  uploadSupportTicketAttachment,
  type SupportTicketDto,
  type SupportTicketExpenseBody,
  type SupportTicketExpenseDto,
  type SupportDeviceRefDto,
  type SupportTicketSummaryDto,
} from "@/lib/api/endpoints/support";
import { hasModulePermission, useEffectivePermissions } from "@/modules/auth/access";
import { useAuthStore } from "@/modules/auth/store";
import { useWorkspaceChromeStore } from "@/store/workspace-chrome";

type CreateFormState = {
  deviceId: string;
  subject: string;
  category: string;
  priority: "low" | "medium" | "high" | "urgent";
  message: string;
};

const emptyForm: CreateFormState = {
  deviceId: "",
  subject: "",
  category: "technical",
  priority: "medium",
  message: "",
};

const surfaceBorder = "var(--card-border)";
const headerBg = "color-mix(in srgb, var(--muted-bg) 55%, var(--card))";

const triggerClassName = tableHeaderSelectTriggerClass;
const menuClassName = themedSelectMenuClass;
const optionClassName = "rounded-control text-caption";
const PAGE_SIZE_OPTIONS = [10, 20, 50, 100] as const;

type ExpenseFormState = {
  category_id: string;
  expense_date: string;
  amount: string;
  currency: string;
  purpose: string;
  vendor: string;
  payment_method: "" | "cash" | "card" | "bank_transfer" | "upi" | "other";
  reference_number: string;
  tax_amount: string;
  is_reimbursable: boolean;
  internal_note: string;
};

const todayDate = () => new Date().toISOString().slice(0, 10);
const emptyExpenseForm = (): ExpenseFormState => ({ category_id: "", expense_date: todayDate(), amount: "", currency: "INR", purpose: "", vendor: "", payment_method: "", reference_number: "", tax_amount: "", is_reimbursable: false, internal_note: "" });

function expenseFormFromDto(expense: SupportTicketExpenseDto): ExpenseFormState {
  return { category_id: expense.category_id, expense_date: expense.expense_date, amount: expense.amount, currency: expense.currency, purpose: expense.purpose, vendor: expense.vendor ?? "", payment_method: expense.payment_method ?? "", reference_number: expense.reference_number ?? "", tax_amount: expense.tax_amount ?? "", is_reimbursable: expense.is_reimbursable, internal_note: expense.internal_note ?? "" };
}

function expensePayload(form: ExpenseFormState): SupportTicketExpenseBody {
  return { category_id: form.category_id, expense_date: form.expense_date, amount: form.amount, currency: form.currency, purpose: form.purpose, vendor: form.vendor || null, payment_method: form.payment_method || null, reference_number: form.reference_number || null, tax_amount: form.tax_amount || null, is_reimbursable: form.is_reimbursable, internal_note: form.internal_note || null };
}

function titleCase(value: string | null | undefined): string {
  if (!value) return "-";
  return value
    .replace(/_/g, " ")
    .replace(/\b\w/g, (char) => char.toUpperCase());
}

function statusTone(status: string): React.CSSProperties {
  const key = status.toLowerCase();
  if (key === "open") return { background: "color-mix(in srgb, var(--success) 12%, var(--card))", color: "var(--success)" };
  if (key === "pending_admin") return { background: "color-mix(in srgb, var(--warning) 14%, var(--card))", color: "var(--warning)" };
  if (key === "pending_requester") return { background: "var(--accent-primary-muted)", color: "var(--accent-primary)" };
  if (key === "resolved") return { background: "var(--semantic-success-surface)", color: "var(--semantic-success)" };
  if (key === "closed") return { background: "var(--surface-secondary)", color: "var(--muted)" };
  return { background: "var(--table-border)", color: "var(--muted)" };
}

function priorityTone(priority: string): React.CSSProperties {
  const key = priority.toLowerCase();
  if (key === "urgent") return { background: "color-mix(in srgb, var(--error) 12%, var(--card))", color: "var(--error)" };
  if (key === "high") return { background: "color-mix(in srgb, var(--warning) 14%, var(--card))", color: "var(--warning)" };
  if (key === "medium") return { background: "color-mix(in srgb, var(--warning) 12%, var(--card))", color: "var(--warning)" };
  return { background: "color-mix(in srgb, var(--success) 12%, var(--card))", color: "var(--success)" };
}

function nextActionLabel(status: SupportTicketSummaryDto["status"]): string {
  if (status === "pending_admin") return "Admin follow-up needed";
  if (status === "pending_requester") return "Requester follow-up needed";
  if (status === "resolved") return "Resolved";
  if (status === "closed") return "Closed";
  return "In progress";
}

function metaValue(user: { name?: string | null; email?: string | null } | null | undefined): string {
  if (!user) return "Unassigned";
  return user.name || user.email || "Unknown";
}

function deviceLabel(device: SupportDeviceRefDto | null | undefined): string {
  if (!device) return "No device selected";
  return device.name?.trim() || device.device_uid;
}

function SupportModal({
  form,
  setForm,
  devices,
  error,
  busy,
  onClose,
  onSubmit,
  attachmentFiles,
  setAttachmentFiles,
}: {
  form: CreateFormState;
  setForm: React.Dispatch<React.SetStateAction<CreateFormState>>;
  devices: SupportDeviceRefDto[];
  error: string | null;
  busy: boolean;
  onClose: () => void;
  onSubmit: () => void;
  attachmentFiles: File[];
  setAttachmentFiles: React.Dispatch<React.SetStateAction<File[]>>;
}) {
  const [errors, setErrors] = useState<{ deviceId?: string; subject?: string; message?: string }>({});

  function handleSubmit() {
    const nextErrors: { deviceId?: string; subject?: string; message?: string } = {};
    if (!form.deviceId) nextErrors.deviceId = "Device is required.";
    if (!form.subject.trim()) nextErrors.subject = "Subject is required.";
    if (!form.message.trim()) nextErrors.message = "Initial message is required.";
    setErrors(nextErrors);
    if (Object.keys(nextErrors).length) return;
    onSubmit();
  }

  const sectionStyle: React.CSSProperties = {
    display: "grid",
    gap: 14,
    padding: 16,
    border: "1px solid var(--table-border)",
    borderRadius: 16,
    background: "var(--muted-bg)",
  };

  return (
    <div
      style={{ position: "fixed", inset: 0, zIndex: 1000, background: "rgba(15, 23, 42, 0.42)", display: "flex", alignItems: "center", justifyContent: "center", padding: 16, overflowY: "auto" }}
      onClick={onClose}
    >
      <div
        style={{ width: "100%", maxWidth: 700, maxHeight: "calc(100vh - 32px)", borderRadius: "var(--radius-card)", border: `1px solid ${surfaceBorder}`, background: "var(--surface-elevated)", boxShadow: "var(--shadow-overlay)", overflow: "hidden", display: "flex", flexDirection: "column" }}
        onClick={(e) => e.stopPropagation()}
      >
        <div style={{ padding: "18px 22px", borderBottom: `1px solid ${surfaceBorder}`, background: headerBg }}>
          <div style={{ fontSize: 18, fontWeight: 700, color: "var(--foreground)" }}>Raise support ticket</div>
          <div style={{ marginTop: 4, fontSize: 12, color: "var(--muted)" }}>Share the issue, impact, and what has already been tried.</div>
        </div>
        <div style={{ padding: 22, display: "grid", gap: 18, overflowY: "auto", flex: 1 }}>
          {error ? (
            <div style={{ border: "1px solid color-mix(in srgb, var(--error) 35%, transparent)", background: "color-mix(in srgb, var(--error) 12%, var(--card))", color: "var(--error)", borderRadius: 12, padding: "10px 12px", fontSize: 12 }}>
              {error}
            </div>
          ) : null}
          <div style={sectionStyle}>
            <label style={{ display: "grid", gap: 8, fontSize: 12, fontWeight: 600 }}>
              <span className="form-label form-label-required">Device</span>
              <ThemedSelect
                value={form.deviceId}
                onChange={(value) => {
                  setForm((p) => ({ ...p, deviceId: value }));
                  setErrors((prev) => ({ ...prev, deviceId: undefined }));
                }}
                options={devices.map((device) => ({
                  value: device.id,
                  label: device.name?.trim() || device.device_uid,
                  description: device.name?.trim() ? device.device_uid : undefined,
                }))}
                placeholder="Select device"
                ariaLabel="Device"
                searchable
                searchPlaceholder="Search devices"
                triggerClassName={triggerClassName}
                menuClassName={menuClassName}
                optionClassName={optionClassName}
              />
              {errors.deviceId ? <span className="form-error">{errors.deviceId}</span> : <span className="form-helper">Choose the logger this issue belongs to.</span>}
              {!devices.length ? <span className="form-helper">No eligible devices are available for support ticket creation in this workspace.</span> : null}
            </label>
          </div>
          <div style={sectionStyle}>
            <label style={{ display: "grid", gap: 8, fontSize: 12, fontWeight: 600 }}>
              <span className="form-label">Attachments</span>
              <input type="file" className="ui-control" multiple accept=".pdf,.txt,.csv,.png,.jpg,.jpeg" onChange={(event) => setAttachmentFiles(Array.from(event.target.files ?? []))} />
              <span className="form-helper">Optional: PDF, TXT, CSV, PNG, or JPEG; up to 15 MB each.</span>
              {attachmentFiles.length ? <span className="form-helper">{attachmentFiles.map((file) => file.name).join(", ")}</span> : null}
            </label>
          </div>
          <div style={sectionStyle}>
            <label style={{ display: "grid", gap: 8, fontSize: 12, fontWeight: 600 }}>
              <span className="form-label form-label-required">Subject</span>
              <input
                className={`ui-control${errors.subject ? " form-field-invalid" : ""}`}
                value={form.subject}
                onChange={(e) => {
                  setForm((p) => ({ ...p, subject: e.target.value }));
                  setErrors((prev) => ({ ...prev, subject: undefined }));
                }}
              />
              {errors.subject ? <span className="form-error">{errors.subject}</span> : <span className="form-helper">Lead with the symptom or outcome the admin should investigate.</span>}
            </label>
          </div>
          <div style={{ ...sectionStyle, gridTemplateColumns: "1fr 1fr", gap: 14 }}>
            <label style={{ display: "grid", gap: 6, fontSize: 12, fontWeight: 600 }}>
              <span className="form-label">Category</span>
              <ThemedSelect
                value={form.category}
                onChange={(value) => setForm((p) => ({ ...p, category: value as CreateFormState["category"] }))}
                options={[
                  { value: "billing", label: "Billing" },
                  { value: "technical", label: "Technical" },
                  { value: "account", label: "Account" },
                  { value: "general", label: "General" },
                ]}
                ariaLabel="Category"
                triggerClassName={triggerClassName}
                menuClassName={menuClassName}
                optionClassName={optionClassName}
              />
            </label>
            <label style={{ display: "grid", gap: 6, fontSize: 12, fontWeight: 600 }}>
              <span className="form-label">Priority</span>
              <ThemedSelect
                value={form.priority}
                onChange={(value) => setForm((p) => ({ ...p, priority: value as CreateFormState["priority"] }))}
                options={[
                  { value: "low", label: "Low" },
                  { value: "medium", label: "Medium" },
                  { value: "high", label: "High" },
                  { value: "urgent", label: "Urgent" },
                ]}
                ariaLabel="Priority"
                triggerClassName={triggerClassName}
                menuClassName={menuClassName}
                optionClassName={optionClassName}
              />
            </label>
          </div>
          <div style={sectionStyle}>
            <label style={{ display: "grid", gap: 8, fontSize: 12, fontWeight: 600 }}>
              <span className="form-label form-label-required">Initial message</span>
              <textarea
                className={`ui-control${errors.message ? " form-field-invalid" : ""}`}
                value={form.message}
                onChange={(e) => {
                  setForm((p) => ({ ...p, message: e.target.value }));
                  setErrors((prev) => ({ ...prev, message: undefined }));
                }}
                style={{ minHeight: 140 }}
              />
              {errors.message ? <span className="form-error">{errors.message}</span> : <span className="form-helper">Include recent steps, timing, business impact, and screenshots/log details if helpful.</span>}
            </label>
          </div>
        </div>
        <div style={{ display: "flex", justifyContent: "flex-end", gap: 10, padding: "18px 22px 22px", borderTop: `1px solid ${surfaceBorder}`, flexShrink: 0 }}>
          <button type="button" onClick={onClose} style={{ minHeight: 38, padding: "0 14px", borderRadius: 12, border: `1px solid ${surfaceBorder}`, background: "var(--card)", cursor: "pointer" }}>
            Cancel
          </button>
          <button type="button" onClick={handleSubmit} disabled={busy || devices.length === 0} style={{ minHeight: 38, padding: "0 14px", borderRadius: 12, border: "1px solid var(--btn-primary-to)", background: "var(--btn-primary-to)", color: "var(--btn-primary-fg)", cursor: "pointer" }}>
            {busy ? "Saving..." : "Create ticket"}
          </button>
        </div>
      </div>
    </div>
  );
}

export function SupportPage() {
  const tenantId = useAuthStore((s) => s.tenantId);
  const user = useAuthStore((s) => s.user);
  const isSuperAdmin = user?.role === "super_admin";
  const isPlatformUser = user?.accountType === "platform";
  const isGlobalSupportActor = isSuperAdmin || isPlatformUser;
  const isCustomer = !isSuperAdmin && !isPlatformUser;
  const { permissionMap } = useEffectivePermissions();
  const canCreateTickets = hasModulePermission(user, permissionMap, "support", "create");
  const canDeleteTickets = hasModulePermission(user, permissionMap, "support", "delete");
  const canManageTickets = (isSuperAdmin || isPlatformUser) && hasModulePermission(user, permissionMap, "support", "edit");
  const qc = useQueryClient();
  const navigate = useNavigate();

  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState<string>("");
  const [assignmentFilter, setAssignmentFilter] = useState<string>("");
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(20);
  const [createOpen, setCreateOpen] = useState(false);
  const [form, setForm] = useState<CreateFormState>(emptyForm);
  const [formError, setFormError] = useState<string | null>(null);
  const [attachmentFiles, setAttachmentFiles] = useState<File[]>([]);
  const [toast, setToast] = useState<string | null>(null);

  const ticketsQuery = useQuery({
    queryKey: ["support", "list", tenantId ?? "all", search, statusFilter, assignmentFilter, page, pageSize],
    queryFn: () =>
      listSupportTickets({
        tenantId,
        search,
        status: statusFilter,
        assignment: assignmentFilter,
        limit: pageSize,
        offset: page * pageSize,
      }),
    enabled: isGlobalSupportActor || Boolean(tenantId),
    staleTime: 10_000,
  });

  const devicesQuery = useQuery({
    queryKey: ["support", "available-devices", tenantId ?? "none", createOpen],
    queryFn: listSupportAvailableDevices,
    enabled: createOpen && isCustomer && canCreateTickets && Boolean(tenantId),
    staleTime: 30_000,
  });

  const assigneesQuery = useQuery({
    queryKey: ["support", "assignees", tenantId ?? "none"],
    queryFn: listSupportAssignees,
    enabled: canManageTickets && (isGlobalSupportActor || Boolean(tenantId)),
    staleTime: 30_000,
  });

  useEffect(() => {
    setPage(0);
  }, [search, statusFilter, assignmentFilter, tenantId, pageSize]);

  const createMutation = useMutation({
    mutationFn: () =>
      createSupportTicket({
        tenant_id: tenantId ?? null,
        device_id: form.deviceId,
        subject: form.subject.trim(),
        category: form.category,
        priority: form.priority,
        initial_message: form.message.trim(),
      }),
    onSuccess: async (ticket) => {
      const uploads = await Promise.allSettled(attachmentFiles.map((file) => uploadSupportTicketAttachment(ticket.id, file)));
      await qc.invalidateQueries({ queryKey: ["support"] });
      qc.setQueryData(["support", "detail", ticket.id], ticket);
      setCreateOpen(false);
      setForm(emptyForm);
      setAttachmentFiles([]);
      setFormError(null);
      setToast(uploads.some((upload) => upload.status === "rejected") ? `Ticket "${ticket.subject}" created, but one or more attachments could not be uploaded.` : `Ticket "${ticket.subject}" created.`);
      navigate(`/support/${ticket.id}`);
    },
    onError: (error) => setFormError(error instanceof Error ? error.message : "Failed to create ticket."),
  });

  const deleteMutation = useMutation({
    mutationFn: (ticketId: string) => deleteSupportTicket(ticketId),
    onSuccess: async () => {
      await qc.invalidateQueries({ queryKey: ["support"] });
      setToast("Ticket deleted.");
    },
  });

  const tickets = ticketsQuery.data?.tickets ?? [];
  const total = ticketsQuery.data?.total ?? 0;
  const pageCount = Math.max(1, Math.ceil(total / pageSize));
  const from = total === 0 ? 0 : page * pageSize + 1;
  const to = Math.min(total, page * pageSize + tickets.length);
  const pageLabel = `Showing ${from} to ${to} of ${total}`;

  function handleCreate() {
    if (!isCustomer || !canCreateTickets) {
      setToast("You don't have permission to create support tickets.");
      return;
    }
    if (!form.deviceId || !form.subject.trim() || !form.message.trim()) {
      setFormError("Device, subject, and initial message are required.");
      return;
    }
    createMutation.mutate();
  }

  return (
    <EnterprisePageShell title="Support" hideHeader>
      <div style={{ display: "grid", gap: 20, padding: 20 }}>
        <ListSurface>
          <ListSurfaceHeader
            title="Support ticket listing"
            description="Search and filter support tickets with server-side pagination."
            meta={<ListCount total={total} noun={total === 1 ? "ticket" : "tickets"} />}
            controls={
              <ListToolbar>
                <ListToolbarGroup grow>
                  <input
                    className={tableHeaderSearchInputClass}
                    value={search}
                    onChange={(event) => setSearch(event.target.value)}
                    placeholder={canManageTickets ? "Search subject, device, requester, assignee..." : "Search subject, device, category, status..."}
                  />
                  <ThemedSelect
                    value={statusFilter}
                    onChange={setStatusFilter}
                    options={[
                      { value: "", label: "All statuses" },
                      { value: "open", label: "Open" },
                      { value: "pending_admin", label: "Pending Admin" },
                      { value: "pending_requester", label: "Pending Requester" },
                      { value: "resolved", label: "Resolved" },
                      { value: "closed", label: "Closed" },
                    ]}
                    ariaLabel="Status filter"
                    triggerClassName={triggerClassName}
                    menuClassName={menuClassName}
                    optionClassName={optionClassName}
                  />
                  {canManageTickets ? (
                    <ThemedSelect
                      value={assignmentFilter}
                      onChange={setAssignmentFilter}
                      options={[
                        { value: "", label: "All assignments" },
                        { value: "mine", label: "Assigned to me" },
                        { value: "unassigned", label: "Unassigned" },
                        ...(assigneesQuery.data ?? []).map((candidate) => ({
                          value: candidate.id,
                          label: candidate.name || candidate.email || candidate.id,
                        })),
                      ]}
                      ariaLabel="Assignment filter"
                      triggerClassName={triggerClassName}
                      menuClassName={menuClassName}
                      optionClassName={optionClassName}
                    />
                  ) : null}
                </ListToolbarGroup>
                {(search || statusFilter || assignmentFilter) ? (
                  <ToolbarIconButton
                    icon={FilterX}
                    label="Clear filters"
                    onClick={() => {
                      setSearch("");
                      setStatusFilter("");
                      setAssignmentFilter("");
                    }}
                  />
                ) : null}
                {isCustomer && canCreateTickets ? (
                  <ToolbarIconButton
                    icon={Plus}
                    label="Raise support ticket"
                    variant="primary"
                    onClick={() => {
                      setCreateOpen(true);
                      setFormError(null);
                    }}
                  />
                ) : null}
              </ListToolbar>
            }
          />
          <ListSurfaceBody className="overflow-x-auto">
            {ticketsQuery.isLoading ? (
              <div className="p-ds-4" aria-label="Loading support tickets">
                {Array.from({ length: 6 }, (_, index) => <SkeletonTableRow key={index} cols={isGlobalSupportActor ? 10 : 9} />)}
              </div>
            ) : ticketsQuery.isError ? (
              <div className="p-4 text-caption text-error" role="alert">
                {ticketsQuery.error instanceof Error ? ticketsQuery.error.message : "Failed to load support tickets."}
              </div>
            ) : tickets.length ? (
              <table className="min-w-full border-collapse">
                <thead className="bg-surface-secondary">
                  <tr>
                    {[
                      "Subject",
                      ...(isGlobalSupportActor ? ["Workspace"] : []),
                      "Device",
                      "Requester",
                      "Assignee",
                      "Status",
                      "Priority",
                      "Updated",
                      "Unread",
                      "Actions",
                    ].map((heading) => (
                      <th key={heading} className="border-b border-card-border px-4 py-3 text-left text-[11px] font-bold uppercase tracking-[0.08em] text-muted">
                        {heading}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {tickets.map((ticket, index) => (
                    <tr key={ticket.id} className={index % 2 === 1 ? "bg-accent-muted/40" : "bg-surface-primary"}>
                      <td className="border-b border-card-border px-4 py-3">
                        <div className="text-sm font-semibold text-foreground">{ticket.subject}</div>
                        <div className="mt-1 text-xs text-muted">{titleCase(ticket.category)} | {ticket.message_count} messages</div>
                      </td>
                      {isGlobalSupportActor ? (
                        <td className="border-b border-card-border px-4 py-3 text-sm text-foreground">{ticket.tenant_name ?? "-"}</td>
                      ) : null}
                      <td className="border-b border-card-border px-4 py-3 text-sm text-foreground">
                        <div>{deviceLabel(ticket.device)}</div>
                        {ticket.device?.name?.trim() ? <div className="mt-1 text-xs text-muted">{ticket.device.device_uid}</div> : null}
                      </td>
                      <td className="border-b border-card-border px-4 py-3 text-sm text-foreground">{metaValue(ticket.opened_by_user)}</td>
                      <td className="border-b border-card-border px-4 py-3 text-sm text-foreground">{metaValue(ticket.assigned_admin_user)}</td>
                      <td className="border-b border-card-border px-4 py-3">
                        <span style={{ display: "inline-flex", padding: "4px 8px", borderRadius: 999, fontSize: 11, fontWeight: 700, ...statusTone(ticket.status) }}>{titleCase(ticket.status)}</span>
                        <div className="mt-1 text-xs text-muted">{nextActionLabel(ticket.status)}</div>
                      </td>
                      <td className="border-b border-card-border px-4 py-3">
                        <span style={{ display: "inline-flex", padding: "4px 8px", borderRadius: 999, fontSize: 11, fontWeight: 700, ...priorityTone(ticket.priority) }}>{titleCase(ticket.priority)}</span>
                      </td>
                      <td className="border-b border-card-border px-4 py-3 text-sm text-foreground">{new Date(ticket.updated_at).toLocaleString()}</td>
                      <td className="border-b border-card-border px-4 py-3 text-sm text-foreground">{ticket.viewer_unread_count}</td>
                      <td className="border-b border-card-border px-4 py-3">
                        <div className="flex items-center justify-end gap-ds-2">
                          <Link
                            className="btn btn-secondary btn-sm inline-flex min-w-9 items-center justify-center px-2"
                            to={`/support/${ticket.id}`}
                            aria-label={`View ${ticket.subject}`}
                            title={`View ${ticket.subject}`}
                          >
                            <Eye className="h-4 w-4" aria-hidden />
                          </Link>
                          {canManageTickets && canDeleteTickets ? (
                            <button
                              type="button"
                              className="btn btn-danger btn-sm inline-flex min-w-9 items-center justify-center px-2"
                              aria-label={`Delete ${ticket.subject}`}
                              title={`Delete ${ticket.subject}`}
                              onClick={() => {
                                if (!confirm(`Delete ticket "${ticket.subject}"?`)) return;
                                deleteMutation.mutate(ticket.id);
                              }}
                            >
                              <Trash2 className="h-4 w-4" aria-hidden />
                            </button>
                          ) : null}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            ) : (
              <div className="p-6">
                <EmptyState title="No support tickets found" description="Adjust search or filters to find matching tickets." />
              </div>
            )}
          </ListSurfaceBody>
          <ListSurfaceFooter
            left={
              <label className="flex items-center gap-ds-2">
                <span className="text-caption text-muted">Rows per page</span>
                <ThemedSelect
                  value={String(pageSize)}
                  ariaLabel="Rows per page"
                  className="w-[5.5rem]"
                  triggerClassName={triggerClassName}
                  menuClassName={menuClassName}
                  optionClassName={optionClassName}
                  options={PAGE_SIZE_OPTIONS.map((option) => ({ value: String(option), label: String(option) }))}
                  onChange={(nextValue) => setPageSize(Number(nextValue))}
                />
              </label>
            }
            right={
              <>
                <span className="text-caption text-muted">Support tickets {pageLabel}</span>
                <button
                  type="button"
                  className="btn btn-secondary btn-sm inline-flex items-center gap-ds-1"
                  disabled={page <= 0}
                  onClick={() => setPage((current) => Math.max(0, current - 1))}
                >
                  <ChevronLeft className="h-4 w-4" aria-hidden />
                  Previous
                </button>
                <span className="text-caption tabular-nums text-muted">
                  Page {Math.min(page + 1, pageCount)} / {pageCount}
                </span>
                <button
                  type="button"
                  className="btn btn-secondary btn-sm inline-flex items-center gap-ds-1"
                  disabled={page + 1 >= pageCount}
                  onClick={() => setPage((current) => current + 1)}
                >
                  Next
                  <ChevronRight className="h-4 w-4" aria-hidden />
                </button>
              </>
            }
          />
        </ListSurface>
      </div>

      {createOpen && canCreateTickets ? (
        <SupportModal
          form={form}
          setForm={setForm}
          devices={devicesQuery.data ?? []}
          error={formError}
          busy={createMutation.isPending}
          onClose={() => {
            setCreateOpen(false);
            setFormError(null);
            setAttachmentFiles([]);
          }}
          onSubmit={handleCreate}
          attachmentFiles={attachmentFiles}
          setAttachmentFiles={setAttachmentFiles}
        />
      ) : null}

      {toast ? <FlashToast message={toast} onDone={() => setToast(null)} /> : null}
    </EnterprisePageShell>
  );
}

export function SupportTicketDetailPage() {
  const { ticketId } = useParams<{ ticketId: string }>();
  const tenantId = useAuthStore((s) => s.tenantId);
  const user = useAuthStore((s) => s.user);
  const isSuperAdmin = user?.role === "super_admin";
  const isPlatformUser = user?.accountType === "platform";
  const isSupportStaff = isSuperAdmin || isPlatformUser;
  const { permissionMap } = useEffectivePermissions();
  const canEditTickets = hasModulePermission(user, permissionMap, "support", "edit");
  const canDeleteTickets = hasModulePermission(user, permissionMap, "support", "delete");
  const canManageTickets = isSupportStaff && canEditTickets;
  const qc = useQueryClient();
  const navigate = useNavigate();
  const setWorkspaceChrome = useWorkspaceChromeStore((state) => state.setWorkspaceChrome);
  const [reply, setReply] = useState("");
  const [toast, setToast] = useState<string | null>(null);
  const [conversationOpen, setConversationOpen] = useState(false);
  const [draftStatus, setDraftStatus] = useState<SupportTicketDto["status"]>("open");
  const [draftAssignee, setDraftAssignee] = useState<string>("");
  const [closingReport, setClosingReport] = useState<File | null>(null);
  const [closingReportError, setClosingReportError] = useState<string | null>(null);
  const [attachmentUpload, setAttachmentUpload] = useState<File | null>(null);
  const [expenseEditor, setExpenseEditor] = useState<SupportTicketExpenseDto | null | undefined>(undefined);
  const [expenseForm, setExpenseForm] = useState<ExpenseFormState>(emptyExpenseForm);
  const [expenseReceiptFile, setExpenseReceiptFile] = useState<File | null>(null);
  const [expenseError, setExpenseError] = useState<string | null>(null);
  const readMarkedForTicketId = useRef<string | null>(null);
  const conversationScrollRef = useRef<HTMLDivElement | null>(null);

  const detailQuery = useQuery({
    queryKey: ["support", "detail", ticketId ?? "none"],
    queryFn: () => getSupportTicket(ticketId!),
    enabled: Boolean(ticketId),
    staleTime: 10_000,
  });

  useLayoutEffect(() => {
    const title = detailQuery.data?.subject.trim() || "Support ticket";
    setWorkspaceChrome({ title, titleAdornment: null, search: null });
    return () => setWorkspaceChrome({ title: "", titleAdornment: null, search: null });
  }, [detailQuery.data?.subject, setWorkspaceChrome]);

  const assigneesQuery = useQuery({
    queryKey: ["support", "assignees", detailQuery.data?.tenant_id ?? tenantId ?? "none"],
    queryFn: listSupportAssignees,
    enabled: canManageTickets && Boolean(detailQuery.data?.tenant_id ?? tenantId),
    staleTime: 30_000,
  });

  const expenseCategoriesQuery = useQuery({
    queryKey: ["support", "expense-categories"],
    queryFn: listSupportExpenseCategories,
    enabled: canManageTickets,
    staleTime: 30_000,
  });

  const canReply = useMemo(() => {
    const detail = detailQuery.data;
    if (!detail) {
      return false;
    }
    if (canManageTickets) {
      return true;
    }
    if (isPlatformUser) {
      return detail.assigned_admin_user_id === user?.id;
    }
    return hasModulePermission(user, permissionMap, "support", "view");
  }, [canManageTickets, detailQuery.data, isPlatformUser, permissionMap, user]);

  useEffect(() => {
    if (!detailQuery.data) return;
    setDraftStatus(detailQuery.data.status);
    setDraftAssignee(detailQuery.data.assigned_admin_user_id ?? "");
    setClosingReport(null);
    setClosingReportError(null);
  }, [detailQuery.data?.id, detailQuery.data?.status, detailQuery.data?.assigned_admin_user_id]);

  const markReadMutation = useMutation({
    mutationFn: (id: string) => markSupportTicketRead(id),
    onSuccess: async (ticket) => {
      qc.setQueryData(["support", "detail", ticket.id], ticket);
      await qc.invalidateQueries({ queryKey: ["support"] });
    },
    onError: () => { readMarkedForTicketId.current = null; },
  });

  useEffect(() => {
    const ticket = detailQuery.data;
    if (!conversationOpen || !ticket || ticket.viewer_unread_count === 0 || readMarkedForTicketId.current === ticket.id) return;
    readMarkedForTicketId.current = ticket.id;
    markReadMutation.mutate(ticket.id);
  }, [conversationOpen, detailQuery.data?.id, detailQuery.data?.viewer_unread_count]);

  useEffect(() => {
    if (!conversationOpen) return;
    const panel = conversationScrollRef.current;
    if (panel) panel.scrollTop = panel.scrollHeight;
  }, [conversationOpen, detailQuery.data?.messages.length]);

  useEffect(() => {
    if (!conversationOpen) return;
    const previousBodyOverflow = document.body.style.overflow;
    const previousDocumentOverflow = document.documentElement.style.overflow;
    document.body.style.overflow = "hidden";
    document.documentElement.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = previousBodyOverflow;
      document.documentElement.style.overflow = previousDocumentOverflow;
    };
  }, [conversationOpen]);

  const reportUploadMutation = useMutation({
    mutationFn: (file: File) => uploadSupportReport(file),
  });

  const updateMutation = useMutation({
    mutationFn: (payload: { ticketId: string; patch: Parameters<typeof updateSupportTicket>[1] }) =>
      updateSupportTicket(payload.ticketId, payload.patch),
    onSuccess: async (ticket) => {
      await qc.invalidateQueries({ queryKey: ["support"] });
      qc.setQueryData(["support", "detail", ticket.id], ticket);
      setToast(`Ticket "${ticket.subject}" updated.`);
    },
  });

  const replyMutation = useMutation({
    mutationFn: () => {
      if (!ticketId) throw new Error("No ticket selected.");
      return addSupportTicketMessage(ticketId, { body: reply.trim() });
    },
    onSuccess: async (ticket) => {
      await qc.invalidateQueries({ queryKey: ["support"] });
      qc.setQueryData(["support", "detail", ticket.id], ticket);
      setReply("");
      setToast("Reply sent.");
    },
  });

  const attachmentMutation = useMutation({
    mutationFn: (file: File) => uploadSupportTicketAttachment(ticketId!, file),
    onSuccess: async () => {
      await qc.invalidateQueries({ queryKey: ["support"] });
      setAttachmentUpload(null);
      setToast("Attachment uploaded.");
    },
  });

  const expenseMutation = useMutation({
    mutationFn: async () => {
      if (!ticketId) throw new Error("No ticket selected.");
      const payload = expensePayload(expenseForm);
      const saved = expenseEditor ? await updateSupportTicketExpense(ticketId, expenseEditor.id, { ...payload, status: expenseEditor.status }) : await createSupportTicketExpense(ticketId, payload);
      if (expenseReceiptFile) await uploadExpenseReceipt(ticketId, saved.id, expenseReceiptFile);
      return saved;
    },
    onSuccess: async () => {
      await qc.invalidateQueries({ queryKey: ["support"] });
      setExpenseEditor(undefined);
      setExpenseReceiptFile(null);
      setExpenseError(null);
      setToast("Expense saved.");
    },
    onError: (error) => setExpenseError(error instanceof Error ? error.message : "Could not save expense."),
  });

  const voidExpenseMutation = useMutation({
    mutationFn: (expense: SupportTicketExpenseDto) => {
      if (!ticketId) throw new Error("No ticket selected.");
      return updateSupportTicketExpense(ticketId, expense.id, { ...expensePayload(expenseFormFromDto(expense)), status: "void" });
    },
    onSuccess: async () => { await qc.invalidateQueries({ queryKey: ["support"] }); setToast("Expense voided. It remains in the ticket history."); },
    onError: (error) => setToast(error instanceof Error ? error.message : "Could not void expense."),
  });

  const deleteMutation = useMutation({
    mutationFn: (id: string) => deleteSupportTicket(id),
    onSuccess: async () => {
      await qc.invalidateQueries({ queryKey: ["support"] });
      navigate("/support");
    },
  });

  const assignableUsers = useMemo(
    () => (assigneesQuery.data ?? []).filter((candidate) => candidate.id !== detailQuery.data?.opened_by_user_id),
    [assigneesQuery.data, detailQuery.data?.opened_by_user_id],
  );

  if (!ticketId) {
    return <Navigate to="/support" replace />;
  }

  const detail = detailQuery.data ?? null;

  async function handleSaveMeta() {
    if (!detail || !canManageTickets) return;
    const patch: Parameters<typeof updateSupportTicket>[1] = {};
    if (draftStatus !== detail.status) patch.status = draftStatus;
    if ((draftAssignee || null) !== (detail.assigned_admin_user_id || null)) patch.assigned_admin_user_id = draftAssignee || null;
    if (draftStatus === "closed" && detail.closed_report_path == null) {
      if (!closingReport) {
        setClosingReportError("Upload a closure report before closing the ticket.");
        return;
      }
      try {
        const uploaded = await reportUploadMutation.mutateAsync(closingReport);
        patch.closed_report_path = uploaded.path;
        patch.closed_report_name = uploaded.file_name;
        patch.closed_report_content_type = uploaded.content_type;
        setClosingReportError(null);
      } catch (error) {
        setClosingReportError(error instanceof Error ? error.message : "Could not upload the closure report.");
        return;
      }
    }
    if (Object.keys(patch).length === 0) {
      setToast("No ticket changes to save.");
      return;
    }
    updateMutation.mutate({ ticketId: detail.id, patch });
  }

  function handleQuickStatus(status: SupportTicketDto["status"]) {
    if (!detail) return;
    updateMutation.mutate({ ticketId: detail.id, patch: { status } });
  }

  function handleAssignToMe() {
    if (!detail || !user?.id) return;
    updateMutation.mutate({ ticketId: detail.id, patch: { assigned_admin_user_id: user.id } });
  }

  async function handleDownloadReport() {
    if (!detail) return;
    try {
      const blob = await downloadSupportReport(detail.id);
      const url = window.URL.createObjectURL(blob);
      const anchor = document.createElement("a");
      anchor.href = url;
      anchor.download = detail.closed_report_name || `support-ticket-${detail.id}-report`;
      document.body.appendChild(anchor);
      anchor.click();
      anchor.remove();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      setToast(error instanceof Error ? error.message : "Could not download the closure report.");
    }
  }

  function handleReply() {
    if (!canReply) {
      setToast("You don't have permission to reply to support tickets.");
      return;
    }
    if (!reply.trim()) return;
    replyMutation.mutate();
  }

  async function handleDownloadAttachment(attachmentId: string, fileName: string) {
    if (!detail) return;
    try {
      const blob = await downloadSupportTicketAttachment(detail.id, attachmentId);
      const url = window.URL.createObjectURL(blob);
      const anchor = document.createElement("a");
      anchor.href = url;
      anchor.download = fileName;
      document.body.appendChild(anchor);
      anchor.click();
      anchor.remove();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      setToast(error instanceof Error ? error.message : "Could not download the attachment.");
    }
  }

  async function handlePreviewAttachment(attachmentId: string) {
    if (!detail) return;
    try {
      const blob = await downloadSupportTicketAttachment(detail.id, attachmentId);
      const url = window.URL.createObjectURL(blob);
      const preview = window.open(url, "_blank");
      if (!preview) {
        window.URL.revokeObjectURL(url);
        setToast("Your browser blocked the attachment preview. Use Download instead.");
        return;
      }
      preview.opener = null;
      window.setTimeout(() => window.URL.revokeObjectURL(url), 60_000);
    } catch (error) {
      setToast(error instanceof Error ? error.message : "Could not preview the attachment.");
    }
  }

  function openExpenseCreate() {
    setExpenseForm(emptyExpenseForm());
    setExpenseReceiptFile(null);
    setExpenseError(null);
    setExpenseEditor(null);
  }

  function openExpenseEdit(expense: SupportTicketExpenseDto) {
    setExpenseForm(expenseFormFromDto(expense));
    setExpenseReceiptFile(null);
    setExpenseError(null);
    setExpenseEditor(expense);
  }

  async function handleDownloadExpenseReceipt(expenseId: string, receiptId: string, fileName: string) {
    if (!detail) return;
    try {
      const blob = await downloadExpenseReceipt(detail.id, expenseId, receiptId);
      const url = window.URL.createObjectURL(blob);
      const anchor = document.createElement("a");
      anchor.href = url;
      anchor.download = fileName;
      document.body.appendChild(anchor);
      anchor.click();
      anchor.remove();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      setToast(error instanceof Error ? error.message : "Could not download the expense receipt.");
    }
  }

  return (
    <EnterprisePageShell title="Support Ticket" hideHeader>
      <div style={{ display: "grid", gap: 20, padding: 20 }}>
        {detailQuery.isLoading ? (
          <ListSurface><div className="p-5 text-sm text-muted">Loading ticket details...</div></ListSurface>
        ) : detailQuery.isError ? (
          <ListSurface><div className="p-5 text-caption text-error" role="alert">{detailQuery.error instanceof Error ? detailQuery.error.message : "Failed to load ticket."}</div></ListSurface>
        ) : detail ? (
          <ListSurface>
            <div style={{ padding: "18px 20px", borderBottom: `1px solid ${surfaceBorder}`, background: headerBg, display: "grid", gap: 16 }}>
              <div style={{ display: "flex", justifyContent: "space-between", gap: 12, alignItems: "flex-start" }}>
                <div>
                  <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
                    <span style={{ display: "inline-flex", padding: "4px 8px", borderRadius: 999, fontSize: 11, fontWeight: 700, ...statusTone(detail.status) }}>{titleCase(detail.status)}</span>
                    <span style={{ display: "inline-flex", padding: "4px 8px", borderRadius: 999, fontSize: 11, fontWeight: 700, ...priorityTone(detail.priority) }}>{titleCase(detail.priority)}</span>
                    <span style={{ fontSize: 12, color: "var(--muted)", alignSelf: "center" }}>{titleCase(detail.category)}</span>
                    <span style={{ fontSize: 12, color: "var(--muted)", alignSelf: "center" }}>{nextActionLabel(detail.status)}</span>
                  </div>
                </div>
                <div style={{ display: "flex", gap: 10, flexWrap: "wrap", justifyContent: "flex-end" }}>
                  <Link className="btn btn-secondary btn-sm" to="/support">Back to list</Link>
                  <button type="button" className="btn btn-secondary btn-sm" onClick={() => setConversationOpen(true)}>
                    <MessageSquare className="h-4 w-4" /> Conversation{detail.viewer_unread_count > 0 ? ` (${detail.viewer_unread_count})` : ""}
                  </button>
                  {canManageTickets && canDeleteTickets ? (
                    <button
                      type="button"
                      onClick={() => {
                        if (!confirm(`Delete ticket "${detail.subject}"?`)) return;
                        deleteMutation.mutate(detail.id);
                      }}
                      style={{ minHeight: 34, padding: "0 12px", borderRadius: 10, border: "1px solid color-mix(in srgb, var(--error) 35%, transparent)", background: "color-mix(in srgb, var(--error) 10%, var(--card))", color: "var(--error)", cursor: "pointer" }}
                    >
                      Delete
                    </button>
                  ) : null}
                </div>
              </div>

              <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(10rem, 1fr))", gap: 12 }}>
                {[
                  { label: "Requester", value: metaValue(detail.opened_by_user), sub: detail.opened_by_user?.email ?? "Workspace member" },
                  { label: "Assignee", value: metaValue(detail.assigned_admin_user), sub: detail.assigned_admin_user?.email ?? "No assignee yet" },
                  {
                    label: "Device",
                    value: detail.device ? <Link to={`/devices/${detail.device.id}`} className="text-foreground hover:text-primary hover:underline">{deviceLabel(detail.device)}</Link> : "No device selected",
                    sub: detail.device ? "Linked support device" : "Required on ticket creation",
                  },
                  { label: "Updated", value: new Date(detail.updated_at).toLocaleString(), sub: detail.last_reply_by_role ? `Last reply by ${titleCase(detail.last_reply_by_role)}` : "No replies yet" },
                  { label: "Unread", value: String(detail.viewer_unread_count), sub: "New updates for you" },
                ].map((item) => (
                  <div key={item.label} style={{ border: `1px solid ${surfaceBorder}`, borderRadius: 16, padding: "12px 14px", background: "var(--card)" }}>
                    <div style={{ fontSize: 11, textTransform: "uppercase", letterSpacing: "0.08em", color: "var(--muted)", fontWeight: 700 }}>{item.label}</div>
                    <div style={{ marginTop: 6, fontSize: 14, fontWeight: 700, color: "var(--foreground)" }}>{item.value}</div>
                    <div style={{ marginTop: 4, fontSize: 12, color: "var(--muted)" }}>{item.sub}</div>
                  </div>
                ))}
              </div>

              {canManageTickets ? (
                <div className="grid gap-ds-4 rounded-card border border-border bg-surface-primary p-ds-4">
                  <div className="flex flex-wrap items-center justify-between gap-ds-2">
                    <div>
                      <div className="text-caption font-semibold text-foreground">Ticket operations</div>
                      <div className="mt-1 text-caption text-muted">Update ownership and lifecycle without leaving the ticket.</div>
                    </div>
                    <button type="button" className="btn btn-primary btn-sm" onClick={() => void handleSaveMeta()} disabled={updateMutation.isPending || reportUploadMutation.isPending}>
                      {updateMutation.isPending || reportUploadMutation.isPending ? "Saving..." : "Save changes"}
                    </button>
                  </div>
                  <div className="grid gap-ds-3 md:grid-cols-2">
                    <label className="grid gap-ds-1 text-caption font-medium">
                      Assignment
                      <ThemedSelect
                        value={draftAssignee}
                        onChange={setDraftAssignee}
                        options={[{ value: "", label: "Unassigned" }, ...assignableUsers.map((candidate) => ({ value: candidate.id, label: candidate.name || candidate.email || candidate.id }))]}
                        ariaLabel="Assigned user"
                        triggerClassName={triggerClassName}
                        menuClassName={menuClassName}
                        optionClassName={optionClassName}
                      />
                    </label>
                    <label className="grid gap-ds-1 text-caption font-medium">
                      Status
                      <ThemedSelect
                        value={draftStatus}
                        onChange={(value) => {
                          setDraftStatus(value as SupportTicketDto["status"]);
                          setClosingReportError(null);
                        }}
                        options={[
                          { value: "open", label: "Open" },
                          { value: "pending_admin", label: "Pending Admin" },
                          { value: "pending_requester", label: "Pending Requester" },
                          { value: "resolved", label: "Resolved" },
                          { value: "closed", label: "Closed" },
                        ]}
                        ariaLabel="Support status"
                        triggerClassName={triggerClassName}
                        menuClassName={menuClassName}
                        optionClassName={optionClassName}
                      />
                    </label>
                  </div>
                  {draftStatus === "closed" ? (
                    <div className="grid gap-ds-2 rounded-control border border-border bg-surface-secondary p-ds-3">
                      <label className="grid gap-ds-1 text-caption font-medium">
                        <span className="form-label form-label-required">Closure report</span>
                        <input
                          type="file"
                          className="ui-control"
                          accept=".pdf,.doc,.docx,.xls,.xlsx,.csv,.txt,.png,.jpg,.jpeg"
                          onChange={(event) => {
                            setClosingReport(event.target.files?.[0] ?? null);
                            setClosingReportError(null);
                          }}
                        />
                      </label>
                      {detail.closed_report_name ? <div className="text-caption text-muted">Existing report: {detail.closed_report_name}</div> : null}
                      {closingReportError ? <div className="form-error">{closingReportError}</div> : null}
                    </div>
                  ) : null}
                  <div className="flex flex-wrap gap-ds-2 border-t border-border pt-ds-3">
                    <button type="button" className="btn btn-secondary btn-sm" onClick={handleAssignToMe} disabled={updateMutation.isPending || detail.assigned_admin_user_id === user?.id}>Assign to me</button>
                    <button type="button" className="btn btn-secondary btn-sm" onClick={() => handleQuickStatus("pending_requester")} disabled={updateMutation.isPending || detail.status === "pending_requester"}>Need requester reply</button>
                    <button type="button" className="btn btn-secondary btn-sm" onClick={() => handleQuickStatus("resolved")} disabled={updateMutation.isPending || detail.status === "resolved"}>Resolve</button>
                    <button type="button" className="btn btn-secondary btn-sm" onClick={() => setDraftStatus("closed")} disabled={updateMutation.isPending || detail.status === "closed"}>Close ticket</button>
                    <button type="button" className="btn btn-secondary btn-sm" onClick={() => handleQuickStatus("open")} disabled={updateMutation.isPending || detail.status === "open"}>Reopen</button>
                  </div>
                </div>
              ) : null}

            </div>

            <div style={{ padding: 20, display: "grid", gridTemplateColumns: "minmax(0, 1.4fr) minmax(280px, 0.9fr)", gap: 18 }}>
              <div style={{ display: "grid", gap: 14 }}>
                {detail.closed_report_name ? (
                  <div style={{ border: `1px solid ${surfaceBorder}`, borderRadius: 18, padding: 16, background: "var(--card)", display: "grid", gap: 10 }}>
                    <div style={{ fontSize: 12, fontWeight: 700, letterSpacing: "0.08em", textTransform: "uppercase", color: "var(--muted)" }}>Closure report</div>
                    <div style={{ fontSize: 14, fontWeight: 700, color: "var(--foreground)" }}>{detail.closed_report_name}</div>
                    <div style={{ fontSize: 12, color: "var(--muted)" }}>{detail.closed_report_content_type || "Uploaded report"}</div>
                    <div>
                      <button type="button" onClick={() => void handleDownloadReport()} style={{ minHeight: 36, padding: "0 12px", borderRadius: 10, border: `1px solid ${surfaceBorder}`, background: "var(--card)", cursor: "pointer" }}>
                        Download report
                      </button>
                    </div>
                  </div>
                ) : null}

                {detail.attachments.length ? (
                  <div className="card grid gap-ds-3 p-ds-4">
                    <div className="flex items-center justify-between gap-ds-2">
                      <div className="text-caption font-bold uppercase tracking-[0.08em] text-muted">Requester attachments</div>
                      <div className="text-micro text-muted">{detail.attachments.length} file{detail.attachments.length === 1 ? "" : "s"}</div>
                    </div>
                    <div className="grid gap-ds-2">
                      {detail.attachments.map((attachment) => {
                        const canPreview = attachment.content_type.startsWith("image/") || attachment.content_type === "application/pdf" || attachment.content_type.startsWith("text/");
                        return (
                          <div key={attachment.id} className="flex flex-wrap items-center justify-between gap-ds-3 rounded-control border border-border bg-surface-secondary px-ds-3 py-ds-2.5">
                            <div className="min-w-0">
                              <div className="truncate text-caption font-semibold text-foreground" title={attachment.file_name}>{attachment.file_name}</div>
                              <div className="mt-1 text-micro text-muted">{attachment.content_type} · {new Date(attachment.created_at).toLocaleDateString()}</div>
                            </div>
                            <div className="flex shrink-0 items-center gap-ds-2">
                              {canPreview ? <button type="button" className="btn btn-secondary btn-sm" onClick={() => void handlePreviewAttachment(attachment.id)}><Eye className="h-3.5 w-3.5" />Open</button> : null}
                              <button type="button" className="btn btn-secondary btn-sm" onClick={() => void handleDownloadAttachment(attachment.id, attachment.file_name)}><Download className="h-3.5 w-3.5" />Download</button>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                ) : null}

                {canManageTickets ? (
                  <div className="card grid gap-ds-3 p-ds-4">
                    <div className="flex flex-wrap items-center justify-between gap-ds-2">
                      <div>
                        <div className="text-caption font-bold uppercase tracking-[0.08em] text-muted">Expenses</div>
                        <div className="mt-1 text-caption text-muted">Optional internal costs for this support ticket.</div>
                      </div>
                      <button type="button" className="btn btn-primary btn-sm" onClick={openExpenseCreate}>Add expense</button>
                    </div>
                    {!detail.expenses.length ? <div className="rounded-control border border-dashed border-border px-ds-3 py-ds-4 text-caption text-muted">No expenses have been recorded for this ticket.</div> : detail.expenses.map((expense) => (
                      <div key={expense.id} className="grid gap-ds-2 rounded-card border border-border p-ds-3">
                        <div className="flex flex-wrap items-start justify-between gap-ds-2">
                          <div><div className="text-body font-semibold text-foreground">{expense.currency} {Number(expense.amount).toFixed(2)}</div><div className="mt-1 text-caption text-muted">{expense.category_name} · {expense.expense_date} · {expense.purpose}</div></div>
                          <span className={expense.status === "void" ? "text-caption font-semibold text-muted" : "text-caption font-semibold text-success"}>{expense.status === "void" ? "Voided" : "Posted"}</span>
                        </div>
                        <div className="flex flex-wrap items-center gap-ds-2">
                          <button type="button" className="btn btn-secondary btn-sm" onClick={() => openExpenseEdit(expense)}>Edit</button>
                          {expense.status === "posted" ? <button type="button" className="btn btn-secondary btn-sm" disabled={voidExpenseMutation.isPending} onClick={() => { if (confirm(`Void ${expense.currency} ${expense.amount} expense?`)) void voidExpenseMutation.mutateAsync(expense); }}>Void</button> : null}
                          {expense.receipts.map((receipt) => <button key={receipt.id} type="button" className="btn btn-secondary btn-sm" onClick={() => void handleDownloadExpenseReceipt(expense.id, receipt.id, receipt.file_name)}>{receipt.file_name}</button>)}
                        </div>
                      </div>
                    ))}
                  </div>
                ) : null}

              </div>

              <div style={{ border: `1px solid ${surfaceBorder}`, borderRadius: 18, padding: 16, background: "var(--card)", display: "grid", gap: 12, alignContent: "start", position: "sticky", top: 88 }}>
                <div style={{ fontSize: 12, fontWeight: 700, letterSpacing: "0.08em", textTransform: "uppercase", color: "var(--muted)" }}>Activity Timeline</div>
                {detail.events.length ? (
                  detail.events.map((event) => (
                    <div key={event.id} style={{ borderLeft: "2px solid var(--accent-primary-muted)", paddingLeft: 12, display: "grid", gap: 4 }}>
                      <div style={{ fontSize: 13, fontWeight: 700, color: "var(--foreground)" }}>{event.title}</div>
                      <div style={{ fontSize: 12, color: "var(--muted)" }}>
                        {(event.actor_name || "System")}{event.actor_role ? ` | ${titleCase(event.actor_role)}` : ""}
                      </div>
                      {event.detail ? <div style={{ fontSize: 12, color: "var(--foreground)", lineHeight: 1.6 }}>{event.detail}</div> : null}
                      <div style={{ fontSize: 11, color: "var(--muted)" }}>{new Date(event.created_at).toLocaleString()}</div>
                    </div>
                  ))
                ) : (
                  <div style={{ fontSize: 12, color: "var(--muted)" }}>No activity recorded yet.</div>
                )}
              </div>
            </div>
          </ListSurface>
        ) : (
          <ListSurface><div className="p-5 text-sm text-muted">Support ticket not found.</div></ListSurface>
        )}
      </div>
      {toast ? <FlashToast message={toast} onDone={() => setToast(null)} /> : null}
      {conversationOpen && detail ? (
        <div className="fixed inset-x-0 bottom-0 top-[var(--topbar-height)] z-[120]" role="presentation">
          <button type="button" className="absolute inset-0 bg-surface-canvas/45 backdrop-blur-[1px]" aria-label="Close conversation" onClick={() => setConversationOpen(false)} />
          <aside className="absolute inset-y-0 right-0 flex w-full max-w-[28rem] flex-col border-l border-card-border bg-surface-elevated shadow-overlay sm:w-[26rem]" role="dialog" aria-modal="true" aria-label="Ticket conversation">
            <div className="flex items-start justify-between gap-ds-3 border-b border-card-border bg-surface-secondary px-ds-4 py-ds-4">
              <div className="min-w-0">
                <div className="flex items-center gap-ds-2">
                  <MessageSquare className="h-4 w-4 text-primary" aria-hidden />
                  <h2 className="text-title text-foreground">Conversation</h2>
                  {detail.viewer_unread_count > 0 ? <span className="rounded-full bg-primary px-2 py-0.5 text-micro font-semibold text-[var(--accent-foreground)]">{detail.viewer_unread_count} new</span> : null}
                </div>
                <p className="mt-1 truncate text-caption text-muted">{detail.subject}</p>
              </div>
              <button type="button" className="btn btn-secondary btn-sm !px-2" aria-label="Close conversation" onClick={() => setConversationOpen(false)}><X className="h-4 w-4" /></button>
            </div>
            <div ref={conversationScrollRef} className="min-h-0 flex-1 overflow-y-auto p-ds-4">
              <div className="grid gap-ds-3">
                {detail.messages.length ? detail.messages.map((message) => {
                  const mine = message.author_user_id === user?.id;
                  return (
                    <div key={message.id} className={mine ? "rounded-card border border-primary/25 bg-primary/10 p-ds-3" : "rounded-card border border-border bg-surface-secondary p-ds-3"}>
                      <div className="flex items-start justify-between gap-ds-2">
                        <div className={mine ? "text-micro font-semibold uppercase tracking-[0.1em] text-primary" : "text-micro font-semibold uppercase tracking-[0.1em] text-muted"}>
                          {message.author} {message.author_role ? ` | ${titleCase(message.author_role)}` : ""}
                        </div>
                        <div className="shrink-0 text-micro text-muted">{new Date(message.created_at).toLocaleString()}</div>
                      </div>
                      <div className="mt-ds-2 whitespace-pre-wrap text-caption leading-relaxed text-foreground">{message.body}</div>
                    </div>
                  );
                }) : <div className="rounded-control border border-dashed border-border px-ds-3 py-ds-4 text-caption text-muted">No messages yet.</div>}
              </div>
            </div>
            <div className="border-t border-card-border bg-surface-secondary p-ds-4">
              {canReply ? (
                <>
                  <label className="text-micro font-semibold uppercase tracking-[0.1em] text-muted">Reply</label>
                  <textarea className="ui-control mt-ds-2 min-h-28 w-full" value={reply} onChange={(event) => setReply(event.target.value)} placeholder={isSupportStaff ? "Reply to the requester..." : "Reply to support..."} />
                  <div className="mt-ds-2 flex justify-end">
                    <button type="button" className="btn btn-primary btn-sm" onClick={handleReply} disabled={replyMutation.isPending || !reply.trim()}>{replyMutation.isPending ? "Sending..." : "Send reply"}</button>
                  </div>
                  {!isSupportStaff ? (
                    <div className="mt-ds-3 flex flex-wrap items-center gap-ds-2">
                      <input type="file" className="ui-control max-w-xs" accept=".pdf,.txt,.csv,.png,.jpg,.jpeg" onChange={(event) => setAttachmentUpload(event.target.files?.[0] ?? null)} />
                      <button type="button" className="btn btn-secondary btn-sm" disabled={!attachmentUpload || attachmentMutation.isPending} onClick={() => attachmentUpload && attachmentMutation.mutate(attachmentUpload)}>{attachmentMutation.isPending ? "Uploading..." : "Upload"}</button>
                    </div>
                  ) : null}
                </>
              ) : <div className="text-caption text-muted">Your role can view this ticket but cannot reply.</div>}
            </div>
          </aside>
        </div>
      ) : null}
      <Modal open={expenseEditor !== undefined} onClose={() => setExpenseEditor(undefined)} title={expenseEditor ? "Edit expense" : "Add expense"} size="md" className="!max-w-2xl">
        <div className="grid gap-ds-3">
          <div className="grid gap-ds-3 sm:grid-cols-2">
            <label className="grid gap-1 text-caption font-medium">Category<ThemedSelect value={expenseForm.category_id} onChange={(value) => setExpenseForm((current) => ({ ...current, category_id: value }))} ariaLabel="Expense category" options={[{ value: "", label: "Select category" }, ...(expenseCategoriesQuery.data ?? []).map((category) => ({ value: category.id, label: category.name }))]} /></label>
            <label className="grid gap-1 text-caption font-medium">Expense date<input type="date" className="ui-control" value={expenseForm.expense_date} onChange={(event) => setExpenseForm((current) => ({ ...current, expense_date: event.target.value }))} /></label>
            <label className="grid gap-1 text-caption font-medium">Amount<input type="number" min="0.01" step="0.01" className="ui-control" value={expenseForm.amount} onChange={(event) => setExpenseForm((current) => ({ ...current, amount: event.target.value }))} /></label>
            <label className="grid gap-1 text-caption font-medium">Currency<input maxLength={3} className="ui-control uppercase" value={expenseForm.currency} onChange={(event) => setExpenseForm((current) => ({ ...current, currency: event.target.value.toUpperCase() }))} /></label>
          </div>
          <label className="grid gap-1 text-caption font-medium">Purpose<textarea rows={2} className="ui-control" value={expenseForm.purpose} onChange={(event) => setExpenseForm((current) => ({ ...current, purpose: event.target.value }))} /></label>
          <div className="grid gap-ds-3 sm:grid-cols-2">
            <label className="grid gap-1 text-caption font-medium">Vendor / payee<input className="ui-control" value={expenseForm.vendor} onChange={(event) => setExpenseForm((current) => ({ ...current, vendor: event.target.value }))} /></label>
            <label className="grid gap-1 text-caption font-medium">Payment method<ThemedSelect value={expenseForm.payment_method} onChange={(value) => setExpenseForm((current) => ({ ...current, payment_method: value as ExpenseFormState["payment_method"] }))} ariaLabel="Payment method" options={[{ value: "", label: "Not specified" }, { value: "cash", label: "Cash" }, { value: "card", label: "Card" }, { value: "bank_transfer", label: "Bank transfer" }, { value: "upi", label: "UPI" }, { value: "other", label: "Other" }]} /></label>
            <label className="grid gap-1 text-caption font-medium">Reference / invoice<input className="ui-control" value={expenseForm.reference_number} onChange={(event) => setExpenseForm((current) => ({ ...current, reference_number: event.target.value }))} /></label>
            <label className="grid gap-1 text-caption font-medium">Tax amount<input type="number" min="0" step="0.01" className="ui-control" value={expenseForm.tax_amount} onChange={(event) => setExpenseForm((current) => ({ ...current, tax_amount: event.target.value }))} /></label>
          </div>
          <label className="flex items-center gap-ds-2 text-caption"><input type="checkbox" checked={expenseForm.is_reimbursable} onChange={(event) => setExpenseForm((current) => ({ ...current, is_reimbursable: event.target.checked }))} /> Reimbursable</label>
          <label className="grid gap-1 text-caption font-medium">Internal note<textarea rows={2} className="ui-control" value={expenseForm.internal_note} onChange={(event) => setExpenseForm((current) => ({ ...current, internal_note: event.target.value }))} /></label>
          <label className="grid gap-1 text-caption font-medium">Receipt (optional)<input type="file" className="ui-control" accept=".pdf,.txt,.csv,.png,.jpg,.jpeg" onChange={(event) => setExpenseReceiptFile(event.target.files?.[0] ?? null)} /></label>
          {expenseError ? <p className="text-caption text-error">{expenseError}</p> : null}
          <div className="flex justify-end gap-ds-2"><button type="button" className="btn btn-secondary" onClick={() => setExpenseEditor(undefined)}>Cancel</button><button type="button" className="btn btn-primary" disabled={!expenseForm.category_id || !expenseForm.expense_date || !expenseForm.amount || Number(expenseForm.amount) <= 0 || !expenseForm.currency || !expenseForm.purpose.trim() || expenseMutation.isPending} onClick={() => void expenseMutation.mutateAsync()}>{expenseMutation.isPending ? "Saving..." : "Save expense"}</button></div>
        </div>
      </Modal>
    </EnterprisePageShell>
  );
}
